import {
  Button,
  Inline,
  Modal,
  type ModalProps,
  Notification,
  notifySuccess,
  useModal,
} from '@omnipresentgroup/design-system';
import { useDeleteCompensationVariantPaymentMutation } from '@omnipresentgroup/domains';
import type { CompensationVariantPayment } from '@omnipresentgroup/types';
import { useEffect, useState } from 'react';

type Props = {
  trigger: ModalProps['trigger'];
  variantPayment: CompensationVariantPayment;
};

export const DeleteVariantPaymentModal = ({
  trigger,
  variantPayment,
}: Props) => {
  const { modalProps, isOpen } = useModal({ trigger });

  const [modalOpen, setModalOpen] = useState(false);

  const {
    mutate: deleteVariantPayment,
    isPending: isDeletingVariantPayment,
    isSuccess,
  } = useDeleteCompensationVariantPaymentMutation({
    employeeId: variantPayment.employeeId,
    id: variantPayment.id,
  });

  // Handle success
  useEffect(() => {
    if (isSuccess) {
      notifySuccess({
        title: 'Variant payment has been deleted successfully',
      });
      setModalOpen(false);
    }
  }, [isSuccess]);

  return (
    <Modal
      {...modalProps}
      span={4}
      open={isOpen || modalOpen}
      onOpenChange={setModalOpen}
    >
      <Modal.Header title={'Delete compensation item'} />
      <Modal.Body>
        <Notification
          id={`delete-variant-payment-notice-${variantPayment.id}`}
          intent="warning"
          title={`Are you certain you want to delete the ${variantPayment.name.toLowerCase()}?`}
        />
      </Modal.Body>
      <Modal.Footer>
        <Inline justify="space-between" w="stretch">
          <Button
            variant="secondary"
            onClick={() => setModalOpen(false)}
            disabled={isDeletingVariantPayment}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteVariantPayment()}
            loading={isDeletingVariantPayment}
          >
            Delete
          </Button>
        </Inline>
      </Modal.Footer>
    </Modal>
  );
};
